import React from 'react';
import { Box, Flex, VStack, createListCollection, Icon } from '@chakra-ui/react';
import { FiBriefcase, FiUser, FiMail, FiPhone, FiMapPin, FiLogOut, FiEdit } from 'react-icons/fi';
import { useTranslation } from '../../hooks/useTranslation';
import { useGetCustomerQuery } from '../../api';
import Button from '../../components/base/Button';
import { InfoItem } from '../../components/profile/InfoItem';
import { InfoSection } from '../../components/profile/InfoSection';
import LoadingState from '../../components/base/LoadingState';
import { FiHash } from 'react-icons/fi';
import { formatAddress } from '../../utils/addressUtils';
import SettingSelectRow from '../../components/mail/SettingSelectRow';
import { modalManager } from '../../utils/modalManager';
import { useLogout } from '../../hooks/useLogout';
import { useSupportEmail } from '../../hooks/useSupportEmail';
import { useInnNotification } from '../../hooks/useInnNotification';
import PageTitle from '../../components/base/PageTitle';

import { useNavigate } from 'react-router-dom';
import { IS_KYC_ENABLED } from '../../api/utils';
import { KYC_ROUTE } from '../kyc/kycRoutes';

const ProfilePage: React.FC = () => {
    const navigate = useNavigate();
    const { t, currentLanguage, changeLanguage, isLoading: isUpdatingLanguage } = useTranslation();
    const { data: customer, isLoading } = useGetCustomerQuery();
    const { logout, isLoading: isLoggingOut } = useLogout();
    const { sendEmail } = useSupportEmail();
    const { isNeedRequestINN, handleSubmitInn } = useInnNotification(false);

    // Коллекция языков для Select
    const languageCollection = React.useMemo(
        () =>
            createListCollection({
                items: [
                    { value: 'ru', label: t.pages.profile.languageNames.ru },
                    { value: 'en', label: t.pages.profile.languageNames.en },
                    { value: 'fr', label: t.pages.profile.languageNames.fr },
                    { value: 'nl', label: t.pages.profile.languageNames.nl },
                    { value: 'de', label: t.pages.profile.languageNames.de },
                ],
            }),
        [t],
    );

    const handleLanguageChange = (language: string) => {
        changeLanguage(language as any);
    };

    const handleEditData = () => {
        modalManager.show({
            title: t.pages.profile.editData,
            message: t.pages.profile.editDataMessage,
            buttons: [
                {
                    type: 'secondary',
                    text: t.pages.mail.cancelButton,
                    onClick: () => {
                        modalManager.remove();
                    },
                },
                {
                    type: 'success',
                    text: t.pages.support.writeButton,
                    onClick: () => {
                        sendEmail(t.pages.support.editDataEmailSubject);
                        modalManager.remove();
                    },
                },
            ],
            size: 'md',
        });
    };

    const handleLogout = () => {
        logout();
    };

    if (isLoading || !customer) {
        return <LoadingState message={t.pages.mail.loading} />;
    }

    // Формируем адрес для пересылки писем
    const mailingAddress = formatAddress(customer.personal_address);

    return (
        <Flex flex={1} minH={0} direction="column" overflow="hidden" bg="gray.100" px="4">
            <Flex flexShrink={0} justify="center">
                <PageTitle title={t.pages.profile.title} />
            </Flex>
            <Flex
                flex={1}
                minH={0}
                w={{ base: 'full', md: '600px' }}
                maxW="4xl"
                mx="auto"
                overflowY="auto"
                direction="column"
            >
                <VStack gap={6} align="stretch" w="full" pt={2} pb={4} mb="100px">
                    {/* Данные компании */}
                    <InfoSection title={t.pages.profile.companyData}>
                        <InfoItem
                            icon={FiBriefcase}
                            label={t.pages.profile.companyName}
                            value={customer.company_name}
                        />
                        {/* ИНН пока нет в API, будет показан когда появится */}
                        <InfoItem icon={FiHash} label={t.pages.profile.inn} value={customer.inn || '--/--'} />
                        {isNeedRequestINN && (
                            <Button buttonType="success" w="full" onClick={handleSubmitInn}>
                                {t.pages.support.innButton}
                            </Button>
                        )}
                        <InfoItem
                            icon={FiMapPin}
                            label={t.pages.profile.officeAddress}
                            value={formatAddress(customer.office_address) || '-'}
                            stacked={true}
                        />
                    </InfoSection>

                    {IS_KYC_ENABLED && (
                        <InfoSection title={t.kyc.title}>
                            <Button buttonType="success" w="full" onClick={() => navigate(KYC_ROUTE)}>
                                {t.kyc.openSection}
                            </Button>
                        </InfoSection>
                    )}

                    {/* Контактное лицо */}
                    <InfoSection title={t.pages.profile.contactPerson}>
                        <InfoItem
                            icon={FiUser}
                            label={t.pages.profile.name}
                            value={`${customer.first_name} ${customer.last_name}`}
                        />
                        <InfoItem icon={FiMail} label={t.pages.profile.email} value={customer.email} />
                        <InfoItem icon={FiPhone} label={t.pages.profile.phone} value={customer.phone} />
                        <InfoItem
                            icon={FiMapPin}
                            label={t.pages.profile.mailingAddress}
                            value={mailingAddress || '-'}
                            stacked={true}
                        />
                    </InfoSection>

                    {/* Настройки */}
                    <InfoSection
                        title={t.pages.profile.settings}
                        gap={4}
                        separator={false}
                        isUpdate={isUpdatingLanguage}
                    >
                        <SettingSelectRow
                            label={t.pages.profile.interfaceLanguage}
                            value={currentLanguage}
                            collection={languageCollection}
                            onValueChange={handleLanguageChange}
                            maxW="200px"
                        />

                        {/* Кнопка редактирования данных */}
                        <Button buttonType="success" w="full" onClick={handleEditData}>
                            <Icon as={FiEdit} boxSize={4} />
                            {t.pages.profile.editData}
                        </Button>

                        {/* Кнопка выхода только для мобильного вида */}
                        <Box display={{ base: 'block', lg: 'none' }}>
                            <Button
                                buttonType="danger"
                                w="full"
                                onClick={handleLogout}
                                disabled={isLoggingOut}
                                isLoading={isLoggingOut}
                            >
                                <Icon as={FiLogOut} boxSize={4} />
                                {t.pages.profile.logoutButton}
                            </Button>
                        </Box>
                    </InfoSection>
                </VStack>
            </Flex>
        </Flex>
    );
};

export default ProfilePage;
